import { useState, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Save } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { LayerDefinition, LayerStyles } from '@packages/database/types';

import { toast } from 'sonner';
import { useUpdateLayerConfig } from '@/hooks/api/useMapLayers';

// =========================================================================
// TYPES
// =========================================================================

// Local form state — LayerStyles + display settings
interface LayerEditorState {
  styles: LayerStyles;
  opacity: number;
  minZoom: number | null;
  maxZoom: number | null;
  // lineDash stored in styles, linePattern is UI-only local state
  linePattern: 'solid' | 'dashed' | 'dotted';
}

// =========================================================================
// HELPERS
// =========================================================================

const ICON_PRESETS = ['/icons/pole.svg', '/icons/house.svg'];

const lineDashToPattern = (dash?: number[]): 'solid' | 'dashed' | 'dotted' => {
  if (!dash || dash.length === 0) return 'solid';
  if (dash[0] >= 8) return 'dashed';
  return 'dotted';
};

const patternToLineDash = (
  pattern: 'solid' | 'dashed' | 'dotted',
): number[] => {
  if (pattern === 'dashed') return [12, 8];
  if (pattern === 'dotted') return [2, 6];
  return [];
};

const initState = (layer: LayerDefinition): LayerEditorState => ({
  styles: layer.styles ?? ({} as LayerStyles),
  opacity: Number(layer.opacity ?? 1),
  minZoom: layer.minZoom ?? null,
  maxZoom: layer.maxZoom ?? null,
  linePattern: lineDashToPattern(layer.styles?.lineDash),
});

// =========================================================================
// COLOR PICKER
// =========================================================================

const ColorPickerInput = ({
  color,
  onChange,
  label,
}: {
  color: string;
  onChange: (c: string) => void;
  label?: string;
}) => (
  <div className="space-y-1.5">
    {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
    <div className="flex gap-2 items-center">
      <Popover modal>
        <PopoverTrigger asChild>
          <div
            className="w-8 h-8 rounded border border-border cursor-pointer shadow-sm shrink-0"
            style={{ backgroundColor: color }}
          />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 border-none shadow-xl">
          <HexColorPicker color={color} onChange={onChange} />
        </PopoverContent>
      </Popover>
      <Input
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 font-mono text-xs"
      />
    </div>
  </div>
);

// =========================================================================
// MAIN COMPONENT
// =========================================================================

interface FeatureLayerEditorProps {
  layer: LayerDefinition;
  onClose: () => void;
}

export const FeatureLayerEditor = ({
  layer,
  onClose,
}: FeatureLayerEditorProps) => {
  const [state, setState] = useState<LayerEditorState>(() => initState(layer));

  // Reset when layer changes
  useEffect(() => {
    setState(initState(layer));
  }, [layer.id]);

  const { updateLayerConfig, isUpdating } = useUpdateLayerConfig(layer.id);

  // =========================================================================
  // STATE HELPERS
  // =========================================================================

  const updateStyles = (updates: Partial<LayerStyles>) => {
    setState((prev) => ({
      ...prev,
      styles: { ...prev.styles, ...updates },
    }));
  };

  const updateGeneral = (
    updates: Partial<Pick<LayerEditorState, 'opacity' | 'minZoom' | 'maxZoom'>>,
  ) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  // =========================================================================
  // SAVE
  // =========================================================================

  const handleSave = () => {
    const { styles, opacity, minZoom, maxZoom } = state;

    updateLayerConfig(
      {
        styles: { ...styles, opacity },
      },
      {
        onSuccess: () => {
          toast.success('Layer updated');
          onClose();
        },
        onError: () => {
          toast.error('Failed to update layer');
        },
      },
    );
  };

  // =========================================================================
  // DERIVED
  // =========================================================================

  const { styles } = state;
  const geomType = layer.geomType?.toUpperCase() ?? '';
  const isPoint = geomType === 'POINT' || geomType === 'MULTIPOINT';
  const isLine = geomType === 'LINESTRING' || geomType === 'MULTILINESTRING';
  const isPolygon = geomType === 'POLYGON' || geomType === 'MULTIPOLYGON';
  const hasStroke = isLine || isPolygon;

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="flex flex-col bg-background rounded-lg overflow-hidden shadow-xl border border-border w-full h-full max-h-[600px]">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/20 flex justify-between items-center shrink-0">
        <h4 className="font-semibold text-sm text-foreground">
          Edit Layer Settings
        </h4>
        <span className="text-[10px] uppercase font-bold text-primary bg-primary/10 px-2 py-0.5 rounded border border-primary/20">
          {geomType || 'N/A'}
        </span>
      </div>

      <Tabs defaultValue="general" className="flex-1 flex flex-col min-h-0">
        <div className="shrink-0 border-b border-border bg-background">
          <TabsList className="w-full grid grid-cols-2 p-0 h-auto bg-transparent">
            {(['general', 'style'] as const).map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className="cursor-pointer rounded-none border-b border-transparent py-3 capitalize data-[state=active]:border-secondary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none transition-all hover:text-foreground text-muted-foreground"
              >
                {tab === 'general' ? 'General' : 'Symbol'}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto p-4 min-h-0 bg-background/50">
          {/* ================= GENERAL TAB ================= */}
          <TabsContent value="general" className="space-y-5 mt-0">
            {/* Opacity */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <Label className="text-xs font-medium text-muted-foreground">
                  Global Opacity
                </Label>
                <span className="text-xs font-mono text-muted-foreground">
                  {(state.opacity * 100).toFixed(0)}%
                </span>
              </div>
              <Slider
                min={0}
                max={1}
                step={0.05}
                value={[state.opacity]}
                onValueChange={([val]) => {
                  updateGeneral({ opacity: val });
                  updateStyles({ opacity: val });
                }}
              />
            </div>

            {/* Zoom Range */}
            <fieldset className="border border-border rounded-md p-3 space-y-3 bg-card/30">
              <legend className="text-xs font-medium px-2 text-muted-foreground">
                Visibility Range (Zoom)
              </legend>
              <div className="grid grid-cols-2 gap-3">
                {(['minZoom', 'maxZoom'] as const).map((key) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-[10px] uppercase text-muted-foreground tracking-wider font-semibold">
                      {key === 'minZoom' ? 'Min Zoom' : 'Max Zoom'}
                    </Label>
                    <Input
                      type="number"
                      className="h-8 bg-background"
                      placeholder={key === 'minZoom' ? '0' : '22'}
                      min={0}
                      max={22}
                      value={state[key] ?? ''}
                      onChange={(e) =>
                        updateGeneral({
                          [key]: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                    />
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground/70">
                Leave empty to show at all zoom levels.
              </p>
            </fieldset>
          </TabsContent>

          {/* ================= STYLE TAB ================= */}
          <TabsContent value="style" className="space-y-5 mt-0">
            {/* POINT */}
            {isPoint && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">
                      Type
                    </Label>
                    <Select
                      value={styles.pointType ?? 'circle'}
                      onValueChange={(val) =>
                        updateStyles({
                          pointType: val as LayerStyles['pointType'],
                        })
                      }
                    >
                      <SelectTrigger className="h-8 bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="circle">Circle</SelectItem>
                        <SelectItem value="square">Square</SelectItem>
                        <SelectItem value="triangle">Triangle</SelectItem>
                        <SelectItem value="star">Star</SelectItem>
                        <SelectItem value="icon">Icon / Symbol</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">
                      Size (px)
                    </Label>
                    <Input
                      type="number"
                      className="h-8 bg-background"
                      min={4}
                      max={64}
                      value={styles.pointSize ?? 8}
                      onChange={(e) =>
                        updateStyles({ pointSize: Number(e.target.value) })
                      }
                    />
                  </div>
                </div>

                {/* Icon options */}
                {styles.pointType === 'icon' && (
                  <div className="p-3 bg-muted/20 border border-border rounded-md space-y-3">
                    <Label className="text-xs font-semibold text-foreground">
                      Icon Source
                    </Label>
                    <div className="grid grid-cols-5 gap-2">
                      {ICON_PRESETS.map((url) => (
                        <button
                          key={url}
                          onClick={() => updateStyles({ iconUrl: url })}
                          className={cn(
                            'w-8 h-8 rounded border flex items-center justify-center transition-all',
                            styles.iconUrl === url
                              ? 'border-secondary bg-secondary/10 ring-1 ring-secondary'
                              : 'border-border bg-background hover:bg-muted',
                          )}
                        >
                          <img
                            src={url}
                            className="w-4 h-4 object-contain"
                            alt="icon"
                          />
                        </button>
                      ))}
                    </div>
                    <div className="pt-2 space-y-2">
                      <div className="flex justify-between">
                        <Label className="text-xs text-muted-foreground">
                          Rotation (°)
                        </Label>
                        <span className="text-xs font-mono text-muted-foreground">
                          {styles.iconRotation ?? 0}°
                        </span>
                      </div>
                      <Slider
                        min={0}
                        max={360}
                        step={15}
                        value={[styles.iconRotation ?? 0]}
                        onValueChange={([val]) =>
                          updateStyles({ iconRotation: val })
                        }
                      />
                    </div>
                  </div>
                )}

                <ColorPickerInput
                  label={
                    styles.pointType === 'icon'
                      ? 'Tint Color (Optional)'
                      : 'Fill Color'
                  }
                  color={styles.color ?? '#3b82f6'}
                  onChange={(c) => updateStyles({ color: c })}
                />
              </div>
            )}

            {/* POLYGON fill */}
            {isPolygon && (
              <div className="space-y-4">
                <ColorPickerInput
                  label="Fill Color"
                  color={styles.fillColor ?? styles.color ?? '#3b82f6'}
                  onChange={(c) => updateStyles({ fillColor: c })}
                />
              </div>
            )}

            {/* LINE / STROKE */}
            {hasStroke && (
              <fieldset className="border-t border-border pt-4 space-y-4">
                <Label className="font-semibold text-xs uppercase text-muted-foreground tracking-wider">
                  {isPolygon ? 'Outline / Stroke' : 'Line Style'}
                </Label>

                <div className="grid grid-cols-2 gap-4">
                  <ColorPickerInput
                    label="Stroke Color"
                    color={styles.strokeColor ?? styles.color ?? '#000000'}
                    onChange={(c) =>
                      updateStyles(isLine ? { color: c } : { strokeColor: c })
                    }
                  />
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">
                      Width (px)
                    </Label>
                    <Input
                      type="number"
                      className="h-8 bg-background"
                      min={1}
                      max={20}
                      value={styles.lineWidth ?? 2}
                      onChange={(e) =>
                        updateStyles({ lineWidth: Number(e.target.value) })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Pattern
                  </Label>
                  <Select
                    value={state.linePattern}
                    onValueChange={(val: 'solid' | 'dashed' | 'dotted') =>
                      setState((prev) => ({
                        ...prev,
                        linePattern: val,
                        styles: {
                          ...prev.styles,
                          lineDash: patternToLineDash(val),
                        },
                      }))
                    }
                  >
                    <SelectTrigger className="h-8 bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid">Solid</SelectItem>
                      <SelectItem value="dashed">Dashed</SelectItem>
                      <SelectItem value="dotted">Dotted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </fieldset>
            )}
          </TabsContent>
        </div>
      </Tabs>

      {/* Footer */}
      <div className="p-3 border-t border-border bg-muted/20 flex justify-end gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          className="bg-background hover:bg-muted"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isUpdating}
          className="bg-secondary text-white hover:bg-secondary/90"
        >
          <Save className="h-4 w-4 mr-2" />
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};
